// external
import React, { FC, useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import StarBorderIcon from '@mui/icons-material/StarBorder';

// internal
import useService from '../../../hooks/service.hook';
import useSnackbarHelper from '../../../hooks/snackbar.hook';
import EquipmentService from '../../../services/equipments.service';
import { getSportMappings } from '../../../helpers/app-context.helpers';

interface EquipmentUsageTabProps {
  equipmentId?: number;
  currentEquipment: any;
  onEquipmentUpdate: (equipment: any) => void;
  onSetRefreshParent: (refresh: boolean) => void;
}

const EquipmentUsageTab: FC<EquipmentUsageTabProps> = ({
  equipmentId,
  currentEquipment,
  onEquipmentUpdate,
  onSetRefreshParent,
}) => {
  const equipmentService = useService(EquipmentService);
  const { showSnackbar } = useSnackbarHelper();

  const sportMappings = useMemo(() => getSportMappings(), []);

  const [selectedSport, setSelectedSport] = useState<string>('');
  const [selectedSportType, setSelectedSportType] = useState<string>('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSelectedSport(currentEquipment?.default_sport?.name ?? '');
    setSelectedSportType(currentEquipment?.default_sport_type?.name ?? '');
  }, [currentEquipment]);

  const sportTypeOptions: string[] = useMemo(() => {
    if (!selectedSport) return [];
    const sport = sportMappings.find((s: any) => s.name === selectedSport);
    return (sport?.sport_types ?? []).map((st: any) => st.name);
  }, [selectedSport, sportMappings]);

  const handleSportChange = (newSport: string) => {
    setSelectedSport(newSport);
    setSelectedSportType('');
  };

  const handleSave = () => {
    if (!equipmentId || !selectedSport || !selectedSportType) return;

    const sport = sportMappings.find((s: any) => s.name === selectedSport);
    const sportType = sport?.sport_types?.find((st: any) => st.name === selectedSportType);

    if (!sport || !sportType) {
      showSnackbar('Could not find matching sport or sport type.', 'error');
      return;
    }

    setSaving(true);
    equipmentService
      .patchEquipmentV2(equipmentId, {
        default_sport_id: sport.id,
        default_sport_type_id: sportType.id,
      })
      .then((res: any) => {
        onSetRefreshParent(true);
        onEquipmentUpdate(res.data.data);
        showSnackbar('Default saved', 'success');
      })
      .catch((err: any) => {
        showSnackbar(err?.response?.data?.message || 'Failed to save. Please try again.', 'error');
      })
      .finally(() => setSaving(false));
  };

  const canSave = Boolean(selectedSport && selectedSportType && !saving);

  return (
    <Box sx={{ width: '100%' }}>
      {/* Default Equipment card */}
      <Box
        sx={{
          border: `1px solid`,
          borderColor: 'divider',
          borderRadius: '12px',
          overflow: 'hidden',
          bgcolor: 'background.paper',
        }}
      >
        {/* Card header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.25,
            px: 2,
            pt: 1.75,
            pb: 1.5,
            borderBottom: `1px solid`,
            borderColor: 'divider',
            bgcolor: (t) => `${t.palette.primary.main}08`,
          }}
        >
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '6px',
              bgcolor: (t) => `${t.palette.primary.main}18`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <StarBorderIcon sx={{ color: 'primary.main', fontSize: 16 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 13.5, lineHeight: 1.3, color: 'text.primary' }}>
              Default Equipment
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: 'text.secondary', lineHeight: 1.4 }}>
              Set this as the default for a sport and discipline
            </Typography>
          </Box>
        </Box>

        {/* Selectors + save */}
        <Box sx={{ px: 2, py: 1.75, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <FormControl required size="small" sx={{ flex: 1 }}>
              <InputLabel id="usage-sport-label">Sport</InputLabel>
              <Select
                labelId="usage-sport-label"
                label="Sport *"
                value={selectedSport}
                onChange={(e) => handleSportChange(e.target.value as string)}
                disabled={saving}
              >
                {sportMappings.map((s: any) => (
                  <MenuItem key={s.id} value={s.name}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl required size="small" sx={{ flex: 1 }} disabled={!selectedSport || saving}>
              <InputLabel id="usage-sport-type-label">Discipline</InputLabel>
              <Select
                labelId="usage-sport-type-label"
                label="Discipline *"
                value={selectedSportType}
                onChange={(e) => setSelectedSportType(e.target.value as string)}
              >
                {sportTypeOptions.map((name: string) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Current default status */}
          {currentEquipment?.default_sport?.name && currentEquipment?.default_sport_type?.name && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  bgcolor: 'success.main',
                  flexShrink: 0,
                }}
              />
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                Currently default for{' '}
                <Typography component="span" sx={{ fontWeight: 600, fontSize: 12, color: 'text.primary' }}>
                  {currentEquipment.default_sport.name}
                </Typography>
                {' · '}
                <Typography component="span" sx={{ fontWeight: 600, fontSize: 12, color: 'text.primary' }}>
                  {currentEquipment.default_sport_type.name}
                </Typography>
              </Typography>
            </Box>
          )}

          <Box>
            <Button
              variant="contained"
              size="small"
              disabled={!canSave}
              onClick={handleSave}
              startIcon={saving ? <CircularProgress size={13} color="inherit" /> : undefined}
              sx={{ textTransform: 'none', fontWeight: 600, fontSize: 12, borderRadius: '8px' }}
            >
              {saving ? 'Saving…' : 'Save Default'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default EquipmentUsageTab;
